import { useEffect, useState } from "react";
import { Plus, Trash2, Save, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { ICONS, THEMES, templateOf } from "@/lib/templates";

const LAYOUTS = [["normal", "Normal article"], ["poem", "Poem / verse (centred lines)"], ["character", "Character-style (kingdom, house & book filters on the section page)"]];
const SHAPES = [["landscape", "Landscape"], ["portrait", "Portrait"], ["shield", "Shield (sigil)"]];
const BLANK = { label: "", icon: "BookOpen", fields: [], theme: "neutral", image_shape: "landscape", layout: "normal", outline: [] };
const lines = (s) => s.split("\n");

const Field = ({ label, hint, children }) => <label className="block"><span className="admin-label">{label}</span>{children}{hint && <span className="block text-xs italic text-[#5e4a35] mt-1">{hint}</span>}</label>;

const IconPicker = ({ value, onChange }) => (
  <div data-testid="template-icon-picker" className="flex flex-wrap gap-1">
    {Object.entries(ICONS).map(([name, Icon]) => (
      <button key={name} type="button" title={name} data-testid={`template-icon-${name}`} onClick={() => onChange(name)}
        className={`p-2 border ${value === name ? "border-[#991b1b] bg-[#991b1b] text-white" : "border-[#c9a227]/50 hover:bg-[#c9a227]/20"}`}><Icon className="w-4 h-4" /></button>
    ))}
  </div>
);

const Form = ({ f, set }) => (
  <div className="grid md:grid-cols-2 gap-5">
    <Field label="Template name"><input data-testid="template-label" className="admin-input" maxLength={60} value={f.label} onChange={(e) => set({ label: e.target.value })} /></Field>
    <Field label="Layout style"><select data-testid="template-layout" className="admin-input" value={f.layout} onChange={(e) => set({ layout: e.target.value })}>{LAYOUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
    <Field label="Default theme"><select data-testid="template-theme" className="admin-input" value={f.theme} onChange={(e) => set({ theme: e.target.value })}>{THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Field>
    <Field label="Fact-panel image shape"><select data-testid="template-shape" className="admin-input" value={f.image_shape} onChange={(e) => set({ image_shape: e.target.value })}>{SHAPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
    <div className="md:col-span-2"><Field label="Icon"><IconPicker value={f.icon} onChange={(icon) => set({ icon })} /></Field></div>
    <Field label="Fact-panel fields" hint="One per line, e.g. Ruler, Capital, Founded">
      <textarea data-testid="template-fields" rows={7} className="admin-input" value={f.fields.join("\n")} onChange={(e) => set({ fields: lines(e.target.value) })} />
    </Field>
    <Field label="Starter outline (optional)" hint="One heading per line. New articles in sections using this template start with these headings.">
      <textarea data-testid="template-outline" rows={7} className="admin-input" value={f.outline.join("\n")} onChange={(e) => set({ outline: lines(e.target.value) })} />
    </Field>
  </div>
);

const TemplateList = ({ list, sel, onPick, onNew }) => (
  <aside className="vellum p-3 h-fit">
    <button data-testid="template-new" onClick={onNew} className="btn-illum theme-flame w-full justify-center mb-3"><Plus className="w-4 h-4" /> New template</button>
    {list.map((t) => {
      const Icon = templateOf(t.key).icon;
      return (
        <button key={t.key} data-testid={`template-item-${t.key}`} onClick={() => onPick(t)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left font-heading text-sm ${sel === t.key ? "bg-[#2d1b0e] text-[#f4efe2]" : "hover:bg-[#c9a227]/20"}`}>
          <Icon className="w-4 h-4 shrink-0" /> <span className="flex-1">{t.label}</span>{t.builtin && <Lock className="w-3 h-3 opacity-60" />}
        </button>
      );
    })}
  </aside>
);

export default function AdminTemplates() {
  const { refresh } = useSite();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/templates").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const pick = (t) => { setSel(t.key); setF({ ...BLANK, ...t }); };
  const current = list.find((t) => t.key === sel);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); await load(); await refresh(); toast.success(msg); return r; } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    const body = { ...f, fields: f.fields.filter((x) => x.trim()), outline: f.outline.filter((x) => x.trim()) };
    const r = await run(() => (sel ? api.put(`/admin/templates/${sel}`, body) : api.post("/admin/templates", body)), "Template saved");
    if (r) pick(r.data);
  };
  const remove = async () => { if (window.confirm(`Delete the "${current.label}" template?`) && await run(() => api.delete(`/admin/templates/${sel}`), "Template deleted")) { setSel(null); setF(BLANK); } };
  const reset = async () => { const r = await run(() => api.post(`/admin/templates/${sel}/reset`), "Template reset to the original"); if (r) pick(r.data); };

  return (
    <div data-testid="admin-templates">
      <AdminTitle eyebrow="Patterns for new pages" title="Article Templates">
        <button data-testid="template-save" onClick={save} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {sel ? "Save template" : "Create template"}</button>
      </AdminTitle>
      <p className="mb-8 italic text-[#4a3a2a] max-w-3xl">A template decides the fact-panel fields, theme, image shape, layout and starter headings for new articles. Pick one when you add or edit a section in Sections. Built-in templates (marked with a lock) can be edited or reset, but not deleted.</p>
      <div className="grid lg:grid-cols-[16rem_1fr] gap-6">
        <TemplateList list={list} sel={sel} onPick={pick} onNew={() => { setSel(null); setF(BLANK); }} />
        <section data-testid="template-form" className="vellum p-6 sm:p-8">
          <p className="eyebrow mb-4">{sel ? `Editing: ${current?.label || ""}` : "New template"}</p>
          <Form f={f} set={set} />
          {sel && (
            <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-[#c9a227]/40">
              {current?.builtin
                ? <button data-testid="template-reset" onClick={reset} disabled={busy} className="btn-ghost-illum"><RotateCcw className="w-4 h-4" /> Reset to original</button>
                : <button data-testid="template-delete" onClick={remove} disabled={busy} className="btn-ghost-illum !text-[#991b1b]"><Trash2 className="w-4 h-4" /> Delete template</button>}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
